<template>
  <div class="rp-page">
    <!-- Header -->
    <header class="rp-header">
      <div class="rp-header__back" @click="goBack">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </div>
      <h1 class="rp-header__title">{{ $lang.rp_title || 'Red Packet Rain' }}</h1>
      <div class="rp-header__right" @click="showHistory = true">
        <van-icon name="clock-o" size="14" color="#ff5a1f" />
        <span>{{ $lang.rp_history || 'History' }}</span>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="loading" class="rp-loading">
      <van-loading size="36" color="#ff5a1f" />
    </div>

    <!-- Not Qualified (receiveStatus=0, 未充值) -->
    <div v-else-if="gameState === 'not_qualified'" class="rp-state rp-state--locked">
      <div class="rp-state__icon-wrap">
        <img src="../../../assets/img/bonus/red_packet.png" class="rp-state__icon rp-state__icon--locked" />
        <van-icon name="lock" size="28" color="#ff5a1f" class="rp-state__lock" />
      </div>
      <h2 class="rp-state__title">{{ $lang.rp_not_qualified || 'Complete a Deposit to Unlock' }}</h2>
      <p class="rp-state__desc">{{ $lang.rp_not_qualified_desc || 'Make a deposit in the last 3 days to participate' }}</p>
      <button class="rp-btn rp-btn--deposit" @click="goDeposit">
        {{ $lang.rp_go_deposit || 'Go Deposit' }}
      </button>
    </div>

    <!-- Activity Not Open (已充值但活动未开启) -->
    <div v-else-if="gameState === 'not_open'" class="rp-state rp-state--locked">
      <img src="../../../assets/img/bonus/red_packet.png" class="rp-state__icon rp-state__icon--grey" />
      <h2 class="rp-state__title">{{ $lang.rp_not_open || 'Activity Not Yet Open' }}</h2>
      <p class="rp-state__desc">{{ $lang.rp_not_open_desc || 'Please check back later' }}</p>
    </div>

    <!-- Countdown (timeLeft > 0) -->
    <div v-else-if="gameState === 'countdown'" class="rp-state rp-state--countdown">
      <img src="../../../assets/img/bonus/red_packet.png" class="rp-state__icon rp-state__icon--pulse" />
      <h2 class="rp-state__title">{{ $lang.rp_countdown || 'Next Round In' }}</h2>
      <div class="rp-countdown">
        <div class="rp-countdown__block">
          <span class="rp-countdown__num">{{ countdownH }}</span>
          <span class="rp-countdown__label">{{ $lang.rp_hour || 'H' }}</span>
        </div>
        <span class="rp-countdown__sep">:</span>
        <div class="rp-countdown__block">
          <span class="rp-countdown__num">{{ countdownM }}</span>
          <span class="rp-countdown__label">{{ $lang.rp_min || 'M' }}</span>
        </div>
        <span class="rp-countdown__sep">:</span>
        <div class="rp-countdown__block">
          <span class="rp-countdown__num">{{ countdownS }}</span>
          <span class="rp-countdown__label">{{ $lang.rp_sec || 'S' }}</span>
        </div>
      </div>
      <p v-if="rpData.nextStartTime" class="rp-state__sub">
        {{ $lang.rp_next_round || 'Next Round' }}: {{ rpData.nextStartTime }}
      </p>
    </div>

    <!-- Rain Active Game -->
    <div v-else-if="gameState === 'active'" class="rp-game">
      <h2 class="rp-game__title">{{ $lang.rp_round_active || 'Tap to Grab!' }}</h2>

      <!-- Stats bar -->
      <div class="rp-game__stats">
        <div class="rp-game__stat">
          <span class="rp-game__stat-label">{{ $lang.rp_clicks || 'Clicks' }}</span>
          <span class="rp-game__stat-value">{{ clickedTimes }} / {{ maxClickTimes }}</span>
        </div>
        <div class="rp-game__stat">
          <span class="rp-game__stat-label">{{ $lang.rp_total_award || 'Total Award' }}</span>
          <span class="rp-game__stat-value rp-game__stat-value--gold">{{ getCurrency }}{{ awardAmount }}</span>
        </div>
      </div>

      <!-- Progress bar -->
      <div class="rp-game__progress">
        <div class="rp-game__progress-track">
          <div class="rp-game__progress-fill" :style="{ width: clickProgress + '%' }"></div>
        </div>
      </div>

      <!-- Rain area -->
      <div class="rp-rain" ref="rainArea">
        <div
          v-for="pkt in packets"
          :key="pkt.id"
          class="rp-rain__packet"
          :class="{ 'rp-rain__packet--popped': pkt.popped }"
          :style="pkt.style"
          @click="grabPacket(pkt)"
        >
          <img :src="packetImg" class="rp-rain__img" />
        </div>
        <!-- Floating reward numbers -->
        <div
          v-for="rw in rewards"
          :key="rw.id"
          class="rp-rain__reward"
          :style="{ left: rw.x + 'px', top: rw.y + 'px' }"
        >
          +{{ getCurrency }}{{ rw.amount }}
        </div>
      </div>
    </div>

    <!-- Already Claimed (receiveStatus=2) -->
    <div v-else-if="gameState === 'claimed'" class="rp-state rp-state--claimed">
      <img src="../../../assets/img/bonus/red_packet.png" class="rp-state__icon" />
      <h2 class="rp-state__title rp-state__title--green">{{ $lang.rp_claimed || 'Claimed This Round' }}</h2>
      <div class="rp-reward-card">
        <span class="rp-reward-card__label">{{ $lang.rp_your_reward || 'Your Reward' }}</span>
        <span class="rp-reward-card__amount">{{ getCurrency }}{{ awardAmount }}</span>
      </div>
      <div v-if="nextTimeLeft > 0" class="rp-next-round">
        <p class="rp-next-round__label">{{ $lang.rp_next_round || 'Next Round' }}</p>
        <span class="rp-next-round__time">{{ formatTime(nextTimeLeft) }}</span>
      </div>
    </div>

    <!-- Round Ended (receiveStatus=3) -->
    <div v-else-if="gameState === 'ended'" class="rp-state rp-state--ended">
      <img src="../../../assets/img/bonus/red_packet.png" class="rp-state__icon rp-state__icon--grey" />
      <h2 class="rp-state__title">{{ $lang.rp_round_ended || 'Round Ended' }}</h2>
      <div v-if="nextTimeLeft > 0" class="rp-next-round">
        <p class="rp-next-round__label">{{ $lang.rp_next_round || 'Next Round' }}</p>
        <span class="rp-next-round__time">{{ formatTime(nextTimeLeft) }}</span>
      </div>
    </div>

    <!-- Round Info Card (visible when not loading) -->
    <div v-if="!loading && rpData" class="rp-info-card">
      <div class="rp-info-card__row" v-if="rpData.startTime">
        <span class="rp-info-card__label">{{ $lang.rp_round_time || 'Round Time' }}</span>
        <span class="rp-info-card__value">{{ formatDateTime(rpData.startTime) }} ~ {{ formatDateTime(rpData.endTime) }}</span>
      </div>
      <div class="rp-info-card__row" v-if="rpData.count !== undefined">
        <span class="rp-info-card__label">{{ $lang.rp_packets_left || 'Packets Left' }}</span>
        <span class="rp-info-card__value">{{ rpData.count }}</span>
      </div>
      <div class="rp-info-card__row" v-if="gameState === 'active' && timeLeft >= 0">
        <span class="rp-info-card__label">{{ $lang.rp_countdown || 'Countdown' }}</span>
        <span class="rp-info-card__value rp-info-card__value--red">{{ formatTime(roundTimeLeft) }}</span>
      </div>
    </div>

    <!-- Rules Section -->
    <div v-if="!loading" class="rp-rules">
      <h3 class="rp-rules__title">{{ $lang.rp_rules_title || 'How to Play' }}</h3>
      <div class="rp-rules__list">
        <div class="rp-rules__item">
          <span class="rp-rules__num">1</span>
          <span>{{ $lang.rp_rule_1 || 'Deposit within the last 3 days to qualify' }}</span>
        </div>
        <div class="rp-rules__item">
          <span class="rp-rules__num">2</span>
          <span>{{ $lang.rp_rule_2 || 'Tap falling red packets to grab rewards' }}</span>
        </div>
        <div class="rp-rules__item">
          <span class="rp-rules__num">3</span>
          <span>{{ $lang.rp_rule_3 || 'Each round has limited clicks' }}</span>
        </div>
      </div>
    </div>

    <!-- History Popup -->
    <van-popup v-model="showHistory" round position="bottom" style="height: 60%; background: #1a1d29;" @open="fetchHistory">
      <div class="rp-history">
        <h3 class="rp-history__title">{{ $lang.rp_history || 'History' }}</h3>
        <div v-if="historyLoading && historyList.length === 0" class="rp-history__empty">
          <van-loading size="36" color="#ff5a1f" />
        </div>
        <div v-else-if="historyList.length === 0" class="rp-history__empty">
          <van-icon name="orders-o" size="48" color="#4b5563" />
          <p>{{ $lang.rp_no_history || 'No records yet' }}</p>
        </div>
        <div v-else class="rp-history__list">
          <div v-for="(item, idx) in historyList" :key="idx" class="rp-history__item">
            <div class="rp-history__left">
              <img src="../../../assets/img/bonus/red_packet.png" width="32" />
            </div>
            <div class="rp-history__mid">
              <span class="rp-history__round">{{ $lang.rp_title || 'Red Packet' }}</span>
              <span class="rp-history__time">{{ item.receiveTime }}</span>
              <span class="rp-history__order">{{ item.recordNo }}</span>
            </div>
            <div class="rp-history__right">
              <span class="rp-history__amount">{{ getCurrency }} +{{ $formatNumberWithCommas(item.awardAmount) }}</span>
            </div>
          </div>
          <div v-if="!historyFinished" class="rp-history__more" @click="loadMoreHistory">
            <span v-if="historyLoading">{{ $lang.common_loading || 'Loading...' }}</span>
            <span v-else>{{ $lang.rp_load_more || 'Load More' }}</span>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { RedPacket, GetRedPacket, RedPacketReceiveRecord } from '@/api/common'
import { reportPromoPanel } from '@/utils/common'
import { safeGetStorageItem, safeLocalStorage } from '@/utils/safeStorage'
import { failMessage as rewardFailMessage, isRewardClaimSuccessWithProof as rewardClaimSuccess, isNeedLogin, isOkResponse, responseContent } from '../activity/rewardApi'
import { hasRewardIssueProof, hasUnavailableMarker } from '../activity/rewardApi'
// Legacy h5-bonus envelope contract name: isClaimSuccess as rewardClaimSuccess.

function getLocalStorage() {
  if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  return null
}

export default {
  name: 'RedPacket',
  data() {
    return {
      loading: true,
      gameState: 'countdown', // not_qualified, countdown, active, claimed, ended
      rpData: null,
      timeLeft: 0,
      nextTimeLeft: 0,
      clickedTimes: 0,
      leftClicks: 0,
      maxClickTimes: 10,
      awardAmount: 0,
      roundKey: '',
      giftUrl: '',
      packets: [],
      rewards: [],
      showHistory: false,
      historyList: [],
      historyLoading: false,
      historyPage: 1,
      historyPageSize: 20,
      historyFinished: false,
      countdownTimer: null,
      rainTimer: null,
      packetIdCounter: 0,
      rewardIdCounter: 0,
      grabbing: false,
      roundTimeLeft: 0,
      roundTimer: null
    }
  },
  computed: {
    countdownH() {
      return String(Math.floor(this.timeLeft / 3600)).padStart(2, '0')
    },
    countdownM() {
      return String(Math.floor((this.timeLeft % 3600) / 60)).padStart(2, '0')
    },
    countdownS() {
      return String(this.timeLeft % 60).padStart(2, '0')
    },
    clickProgress() {
      const max = Number(this.maxClickTimes)
      const clicked = Number(this.clickedTimes)
      if (!Number.isFinite(max) || max <= 0 || !Number.isFinite(clicked) || clicked <= 0) return 0
      return Math.min((clicked / max) * 100, 100)
    },
    packetImg() {
      return this.giftUrl || require('../../../assets/img/bonus/red_packet.png')
    },
    token() {
      return safeGetStorageItem(getLocalStorage(), 'token')
    }
  },
  mounted() {
    if (!this.token) {
      this.$bus.$emit('openLogin')
      return
    }
    reportPromoPanel('red_packet', 1)
    this.initRedPacket()
  },
  beforeDestroy() {
    reportPromoPanel('red_packet', 2)
    this.clearTimers()
  },
  methods: {
    requireLogin() {
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    isRewardNeedLogin(res) {
      if (isNeedLogin(res)) return true
      const stack = [res]
      for (let i = 0; i < stack.length; i++) {
        const body = stack[i]
        if (!body || typeof body !== 'object') continue
        if (body.need_login === true || body.needLogin === true || [body.status, body.state, body.code, body.errorCode, body.need_login, body.needLogin].some(value => String(value || '').trim().toLowerCase() === 'need_login')) return true
        ;['content', 'data', 'page', 'extra'].forEach((key) => {
          const value = body[key]
          if (value && typeof value === 'object') stack.push(value)
        })
      }
      return false
    },
    hasRewardUnavailableMarker(res) {
      if (hasUnavailableMarker(res)) return true
      const stack = [res]
      for (let i = 0; i < stack.length && i < 50; i++) {
        const body = stack[i]
        if (!body || typeof body !== 'object') continue
        if (Array.isArray(body)) {
          body.forEach((value) => {
            if (value && typeof value === 'object') stack.push(value)
          })
          continue
        }
        const values = [
          body.readOnly,
          body.readOnlyMode,
          body.read_only_mode,
          body.readOnlyReward,
          body.read_only_reward,
          body.readOnlyAction,
          body.read_only_action,
          body.read_only,
          body.mock_fallback,
          body.not_implemented,
          body.not_supported,
          body.need_login,
          body.needLogin,
          body.notSupported,
          body.unsupported,
          body.technicalMarker,
          body.technical_marker,
          body.backendFailure,
          body.backend_failure,
          body.technicalError,
          body.technical_error,
          body.technicalException,
          body.technical_exception,
          body.fallback,
          body.dataUnavailable,
          body.data_unavailable,
          body.readUnavailable,
          body.read_unavailable,
          body.writeBlocked,
          body.readBlocked,
          body.read_blocked,
          body.backendBlocked,
          body.backend_blocked,
          body.rewardBackendBlocked,
          body.reward_backend_blocked,
          body.falseProof,
          body.false_proof,
          body.failClosed,
          body.fail_closed,
          body.unimplemented,
          body.failed,
          body.readonly,
          body.notImplemented,
          body.mock,
          body.mockMode,
          body.mock_mode,
          body.mockFallback,
          body.write_blocked,
          body.stub,
          body.msg,
          body.message,
          body.reason,
          body.desc,
          body.description,
          body.errorMsg,
          body.errorMessage,
          body.detail,
          body.readOnlyMessage,
          body.unavailableReason,
          body.status,
          body.state,
          body.result,
          body.code,
          body.errorCode,
          body.paymentStatusUnconfirmed,
          body.payment_status_unconfirmed,
          body.paymentStatus,
          body.payment_status,
          body.rewardStatus,
          body.reward_status
        ]
        const proofValues = [
          body.writeProof,
          body.write_proof,
          body.rewardProof,
          body.reward_proof,
          body.orderProof,
          body.order_proof,
          body.affectedProof,
          body.affected_proof,
          body.serverProof,
          body.server_proof
        ]
        if (proofValues.some((value) => {
          const token = String(value == null ? '' : value).trim().toLowerCase()
          return value === false || value === 0 || token === 'false' || token === '0' || token === 'fail' || token === 'failed'
        })) return true
        const markers = ['未接入', 'mock', 'mockMode', 'mock_mode', 'fallback', 'dataUnavailable', 'data unavailable', 'data_unavailable', 'readUnavailable', 'read unavailable', 'read_unavailable', 'readBlocked', 'read blocked', 'read_blocked', 'stub', '只读', 'readOnlyMode', 'read_only_mode', 'readOnlyReward', 'read_only_reward', 'readOnlyAction', 'read_only_action', '阻止假成功', 'writeBlocked', 'paymentStatusUnconfirmed', 'payment_status_unconfirmed', 'unconfirmed', 'writeProof', 'write_proof', 'writeProof:false', 'write_proof:false', 'falseProof', 'false_proof', 'false proof', 'failClosed', 'fail_closed', 'fail closed', 'backendBlocked', 'rewardBackendBlocked', 'backend_blocked', 'reward_backend_blocked', 'technicalMarker', 'technical_marker', 'technical marker', 'backendFailure', 'backend_failure', 'backend failure', 'technicalException', 'technical_exception', 'technical exception', 'technicalError', 'technical_error', 'technical error', 'not_implemented', 'not_supported', 'not supported', 'need_login', 'need login', 'need-login', 'unsupported', 'unimplemented', 'failed']
        if (values.some((value) => {
          if (value === true || value === 1) return true
          const token = String(value == null ? '' : value).trim().toLowerCase()
          return token && markers.some((marker) => token.includes(String(marker).toLowerCase()))
        })) return true
        ;['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].forEach((key) => {
          const value = body[key]
          if (value && typeof value === 'object') stack.push(value)
        })
      }
      return false
    },
    failMessage(res, fallback) {
      return rewardFailMessage(res, fallback || this.$lang.rp_round_ended_msg || 'This feature is temporarily unavailable. Please try again later.')
    },
    safeNumber(value, fallback = 0) {
      const num = Number(value)
      return Number.isFinite(num) ? num : fallback
    },
    safeInteger(value, fallback = 0) {
      const num = parseInt(value, 10)
      return Number.isFinite(num) ? num : fallback
    },
    isRewardWriteDisabled(content, source) {
      const c = content || {}
      return this.hasRewardUnavailableMarker(source || c) ||
        this.hasRewardUnavailableMarker(c) ||
        c.rewardWritable === false ||
        String(c.rewardWritable).trim().toLowerCase() === 'false' ||
        c.writeEnabled === false ||
        String(c.writeEnabled).trim().toLowerCase() === 'false'
    },
    normalizePacketReceiveStatus(content, source) {
      const c = content || {}
      const rawStatus = this.safeInteger(
        c.rawReceiveStatus === undefined ? c.receiveStatus : c.rawReceiveStatus,
        this.safeInteger(c.receiveStatus, 0)
      )
      if (rawStatus === 1 && this.isRewardWriteDisabled(c, source)) return 0
      return rawStatus
    },
    normalizePacketContent(content, source) {
      const c = content || {}
      return {
        ...c,
        receiveStatus: this.normalizePacketReceiveStatus(c, source),
        timeLeft: Math.max(this.safeInteger(c.timeLeft, 0), 0),
        nextTimeLeft: Math.max(this.safeInteger(c.nextTimeLeft, 0), 0),
        clickedTimes: Math.max(this.safeInteger(c.clickedTimes, 0), 0),
        leftClicks: Math.max(this.safeInteger(c.leftClicks, 0), 0),
        maxClickTimes: Math.max(this.safeInteger(c.maxClickTimes, 10), 1),
        awardAmount: Math.max(this.safeNumber(c.awardAmount, 0), 0),
        roundKey: c.roundKey ? String(c.roundKey) : '',
        giftUrl: c.giftUrl || ''
      }
    },
    hasPacketReadData(content) {
      const c = content || {}
      return [
        'receiveStatus',
        'rawReceiveStatus',
        'timeLeft',
        'nextTimeLeft',
        'clickedTimes',
        'leftClicks',
        'maxClickTimes',
        'awardAmount',
        'roundKey',
        'startTime',
        'endTime',
        'nextStartTime',
        'count',
        'rechargeSatisfied'
      ].some(key => Object.prototype.hasOwnProperty.call(c, key))
    },
    hasRechargeSatisfied(content) {
      if (!Object.prototype.hasOwnProperty.call(content, 'rechargeSatisfied')) return true
      return content.rechargeSatisfied !== false && Number(content.rechargeSatisfied) !== 0
    },
    hasHistoryReadData(content) {
      const c = content || {}
      return [
        'list',
        'records',
        'dataList',
        'recordList',
        'pageList',
        'items',
        'rows',
        'data'
      ].some(key => Array.isArray(c[key])) ||
        [
          'total',
          'totalCount',
          'pageTotalCount',
          'pageCount',
          'pageIndex',
          'pageSize'
        ].some(key => Object.prototype.hasOwnProperty.call(c, key))
    },
    extractHistoryList(content) {
      const c = content || {}
      const list = c.list ||
        c.records ||
        c.dataList ||
        c.recordList ||
        c.pageList ||
        c.items ||
        c.rows ||
        c.data ||
        []
      if (!Array.isArray(list)) return []
      return list.map(item => {
        const record = item || {}
        return {
          ...record,
          receiveTime: record.receiveTime || record.createTime || record.createdAt || '',
          recordNo: record.recordNo || record.orderNo || record.roundKey || '',
          awardAmount: Math.max(this.safeNumber(record.awardAmount || record.amount || record.reward, 0), 0)
        }
      })
    },
    historyRecordKey(item) {
      const record = item || {}
      return [
        record.recordNo,
        record.receiveTime,
        record.awardAmount
      ].map(value => value === undefined || value === null ? '' : String(value)).join('|')
    },
    mergeHistoryList(existing, incoming) {
      const merged = []
      const seen = new Set()
      ;[...(existing || []), ...(incoming || [])].forEach(item => {
        const key = this.historyRecordKey(item)
        if (key && seen.has(key)) return
        if (key) seen.add(key)
        merged.push(item)
      })
      return merged
    },
    hasRewardIssueProof(res) {
      return hasRewardIssueProof(res)
    },
    hasRewardWriteProof(res) {
      if (this.hasRewardUnavailableMarker(res)) return false
      const stack = [responseContent(res), res]
      for (let i = 0; i < stack.length && i < 50; i++) {
        const body = stack[i]
        if (!body || typeof body !== 'object') continue
        if (Array.isArray(body)) {
          body.forEach((value) => {
            if (value && typeof value === 'object') stack.push(value)
          })
          continue
        }
        const proofs = [
          body.writeProof,
          body.write_proof,
          body.affectedProof,
          body.affected_proof,
          body.affectedRowsProof,
          body.affected_rows_proof,
          body.count,
          body.serverProof,
          body.server_proof,
          body.serverRewardProof,
          body.server_reward_proof,
          body.rewardIssueProof,
          body.reward_issue_proof
        ]
        if (proofs.some((value) => {
          const token = String(value == null ? '' : value).trim().toLowerCase()
          return value === false || value === 0 || token === 'false' || token === '0' || token === 'fail' || token === 'failed'
        })) return false
        const positiveProofs = [
          body.rewardProof,
          body.reward_proof,
          body.rewardIssueProof,
          body.reward_issue_proof
        ]
        if (positiveProofs.some((value) => {
          const token = String(value == null ? '' : value).trim().toLowerCase()
          return value === true || value === 1 || token === 'true' || token === '1' || token === 'success' || token === 'ok'
        })) return true
        const recordProofs = [
          body.recordNo,
          body.record_no,
          body.recordId,
          body.record_id,
          body.rewardRecordNo,
          body.reward_record_no,
          body.rewardRecordId,
          body.reward_record_id,
          body.receiveRecordNo,
          body.receive_record_no,
          body.receiveRecordId,
          body.receive_record_id,
          body.claimRecordNo,
          body.claim_record_no,
          body.claimRecordId,
          body.claim_record_id
        ]
        const hasRecordProof = recordProofs.some((value) => {
          if (value && typeof value === 'object') return false
          const token = String(value == null ? '' : value).trim().toLowerCase()
          return token && !['true', '1', 'success', 'ok', 'false', '0', 'fail', 'failed', 'none', 'null', 'undefined', 'placeholder', 'mock', 'mockmode', 'mock_mode', 'mockfallback', 'mock_fallback', 'stub', 'fallback', 'fallbackused', 'fallback_used', 'readonly', 'readonlymode', 'read_only', 'read_only_mode', 'readonlyreward', 'read_only_reward', 'readonlyaction', 'read_only_action', 'writeblocked', 'write_blocked', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'unconfirmed', 'backendblocked', 'backend_blocked', 'rewardbackendblocked', 'reward_backend_blocked', 'falseproof', 'false_proof', 'readunavailable', 'read_unavailable', 'routeunavailable', 'route_unavailable', 'technicalmarker', 'technical_marker', 'backendfailure', 'backend_failure', 'technicalerror', 'technical_error', 'unimplemented', 'notimplemented', 'not_implemented', 'notsupported', 'not_supported'].includes(token)
        })
        const aliasProofs = [
          body.serverRewardProof,
          body.server_reward_proof
        ]
        if (hasRecordProof && aliasProofs.some((value) => {
          const token = String(value == null ? '' : value).trim().toLowerCase()
          return value === true || value === 1 || token === 'true' || token === '1' || token === 'success' || token === 'ok'
        })) return true
        const concreteProofs = [
          body.rewardProof,
          body.reward_proof,
          body.serverRewardProof,
          body.server_reward_proof,
          body.rewardIssueProof,
          body.reward_issue_proof
        ]
        if (concreteProofs.some((value) => {
          if (value && typeof value === 'object') return false
          const token = String(value == null ? '' : value).trim().toLowerCase()
          return token && !['true', '1', 'success', 'ok', 'false', '0', 'fail', 'failed', 'none', 'null', 'undefined', 'placeholder', 'mock', 'mockmode', 'mock_mode', 'mockfallback', 'mock_fallback', 'stub', 'fallback', 'fallbackused', 'fallback_used', 'readonly', 'readonlymode', 'read_only', 'read_only_mode', 'readonlyreward', 'read_only_reward', 'readonlyaction', 'read_only_action', 'writeblocked', 'write_blocked', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'unconfirmed', 'backendblocked', 'backend_blocked', 'rewardbackendblocked', 'reward_backend_blocked', 'falseproof', 'false_proof', 'readunavailable', 'read_unavailable', 'routeunavailable', 'route_unavailable', 'technicalmarker', 'technical_marker', 'backendfailure', 'backend_failure', 'technicalerror', 'technical_error', 'unimplemented', 'notimplemented', 'not_implemented', 'notsupported', 'not_supported'].includes(token)
        })) return true
        ;['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'detail', 'details'].forEach((key) => {
          const value = body[key]
          if (value && typeof value === 'object') stack.push(value)
        })
      }
      return false
    },
    hasExplicitRewardSuccess(res) {
      const c = responseContent(res)
      return [
        c.receiveSuccess,
        c.claimSuccess,
        c.signSuccess,
        c.paySuccess,
        c.success,
        c.received,
        c.claimed,
        c.signed
      ].some(value => value === true || value === 1 || ['1', 'true', 'success', 'ok'].includes(String(value == null ? '' : value).trim().toLowerCase()))
    },
    isCollectSuccess(res) {
      return !this.hasRewardUnavailableMarker(res) && rewardClaimSuccess(res) && this.hasExplicitRewardSuccess(res) && (this.hasRewardIssueProof(res) || this.hasRewardWriteProof(res)) && this.hasRewardWriteProof(res)
    },
    hasExistingRedPacketState() {
      return !!this.rpData || this.gameState !== 'countdown' || this.roundKey || this.clickedTimes > 0 || this.awardAmount > 0
    },
    preserveOrCloseRedPacketState() {
      if (!this.hasExistingRedPacketState()) {
        this.gameState = 'not_open'
      }
    },
    async initRedPacket() {
      this.loading = true
      try {
        const res = await RedPacket({})
        if (isOkResponse(res) && this.hasPacketReadData(responseContent(res))) {
          const c = this.normalizePacketContent(responseContent(res))
          c.receiveStatus = this.normalizePacketReceiveStatus(c, res)
          this.rpData = c
          this.roundKey = c.roundKey
          const receiveStatus = c.receiveStatus
          this.timeLeft = c.timeLeft
          this.nextTimeLeft = c.nextTimeLeft
          this.clickedTimes = c.clickedTimes
          this.leftClicks = c.leftClicks
          this.maxClickTimes = c.maxClickTimes
          this.awardAmount = c.awardAmount
          this.giftUrl = c.giftUrl

          // 状态判定（依据后端 rechargeSatisfied）：
          // ① !rechargeSatisfied           → 引导充值
          // ② receiveStatus===2            → 已领取本轮
          // ③ receiveStatus===1 && timeLeft===0 → 可参与
          // ④a timeLeft>0                  → 下一轮倒计时
          // ④b 其他（含 receiveStatus===3）→ 活动/场次未开
          if (!this.hasRechargeSatisfied(c)) {
            this.gameState = 'not_qualified'
          } else if (receiveStatus === 2) {
            this.gameState = 'claimed'
            this.startNextCountdown()
          } else if (receiveStatus === 1 && this.timeLeft === 0) {
            this.gameState = 'active'
            this.calcRoundTimeLeft()
            this.startRoundTimer()
            this.startRain()
          } else if (this.timeLeft > 0) {
            this.gameState = 'countdown'
            this.startCountdown()
          } else {
            this.gameState = 'not_open'
          }
        } else if (this.isRewardNeedLogin(res)) {
          this.requireLogin()
        } else {
          this.preserveOrCloseRedPacketState()
          this.$toast({
            message: this.failMessage(res),
            icon: 'cross'
          })
        }
      } catch (e) {
        this.preserveOrCloseRedPacketState()
        this.$toast({ message: this.failMessage(e, this.$lang.network_error || '活动请求失败，请稍后重试。'), icon: 'cross' })
      } finally {
        this.loading = false
      }
    },

    clearTimers() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
      if (this.rainTimer) {
        clearInterval(this.rainTimer)
        this.rainTimer = null
      }
      if (this.roundTimer) {
        clearInterval(this.roundTimer)
        this.roundTimer = null
      }
    },

    startCountdown() {
      this.clearTimers()
      this.countdownTimer = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--
        } else {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
          // Re-fetch to check if round is now active
          this.initRedPacket()
        }
      }, 1000)
    },

    startNextCountdown() {
      if (this.nextTimeLeft <= 0) return
      this.clearTimers()
      this.countdownTimer = setInterval(() => {
        if (this.nextTimeLeft > 0) {
          this.nextTimeLeft--
        } else {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
          this.initRedPacket()
        }
      }, 1000)
    },

    // Rain game logic
    startRain() {
      this.packets = []
      this.rewards = []
      // Spawn packets periodically
      this.spawnBatch()
      this.rainTimer = setInterval(() => {
        this.spawnBatch()
        // Clean up old packets
        this.packets = this.packets.filter(p => !p.expired && !p.popped)
      }, 2000)
    },

    spawnBatch() {
      const count = 4 + Math.floor(Math.random() * 4) // 4-7 packets
      for (let i = 0; i < count; i++) {
        this.packetIdCounter++
        const left = 5 + Math.random() * 80 // 5% - 85%
        const delay = Math.random() * 1.5
        const duration = 3 + Math.random() * 2 // 3-5s fall time
        const size = 36 + Math.random() * 20 // 36-56px
        const pkt = {
          id: this.packetIdCounter,
          popped: false,
          expired: false,
          style: {
            left: left + '%',
            animationDelay: delay + 's',
            animationDuration: duration + 's',
            width: size + 'px',
            height: size + 'px'
          }
        }
        this.packets.push(pkt)
        // Auto-expire after animation
        setTimeout(() => {
          pkt.expired = true
        }, (delay + duration) * 1000 + 500)
      }
    },

    async grabPacket(pkt) {
      if (pkt.popped || this.grabbing) return
      if (!this.token) {
        this.requireLogin()
        return
      }
      if (this.clickedTimes >= this.maxClickTimes) return

      pkt.popped = true
      this.clickedTimes++

      // Show floating animation for this click
      this.rewardIdCounter++
      const curId = this.rewardIdCounter
      const rainArea = this.$refs.rainArea
      const rect = rainArea ? rainArea.getBoundingClientRect() : { width: 300, height: 400 }
      this.rewards.push({
        id: curId,
        amount: '?',
        x: parseFloat(pkt.style.left) / 100 * rect.width,
        y: rect.height * 0.4
      })
      setTimeout(() => {
        this.rewards = this.rewards.filter(r => r.id !== curId)
      }, 1500)

      // When all clicks used, call collect API once
      if (this.clickedTimes >= this.maxClickTimes) {
        this.grabbing = true
        try {
          const res = await GetRedPacket({ roundKey: this.roundKey })
          if (this.isRewardNeedLogin(res)) {
            this.requireLogin()
            this.initRedPacket()
          } else if (this.isCollectSuccess(res)) {
            const c = this.normalizePacketContent(responseContent(res))
            c.receiveStatus = this.normalizePacketReceiveStatus(c, res)
            const receiveStatus = c.receiveStatus
            this.awardAmount = c.awardAmount
            if (receiveStatus === 3) {
              this.gameState = 'ended'
            } else {
              this.gameState = 'claimed'
            }
            this.clearTimers()
            this.nextTimeLeft = c.nextTimeLeft
            this.startNextCountdown()
          } else {
            // 本场已被领完或其它错误：提示并切到 ended 状态
            this.$toast({
              message: this.failMessage(res, this.$lang.rp_round_ended_msg || 'Red packets are all grabbed'),
              icon: 'cross'
            })
            this.awardAmount = 0
            this.gameState = 'ended'
            this.clearTimers()
            this.initRedPacket()
          }
        } catch (e) {
          this.$toast({ message: this.failMessage(e, this.$lang.network_error || '活动请求失败，请稍后重试。'), icon: 'cross' })
          this.gameState = 'ended'
          this.clearTimers()
        } finally {
          this.grabbing = false
        }
      }
    },

    formatTime(seconds) {
      const safeSeconds = Math.max(this.safeInteger(seconds, 0), 0)
      const h = String(Math.floor(safeSeconds / 3600)).padStart(2, '0')
      const m = String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, '0')
      const s = String(safeSeconds % 60).padStart(2, '0')
      return h + ':' + m + ':' + s
    },

    // Format API time strings or timestamps to readable HH:MM
    formatDateTime(val) {
      if (!val) return ''
      if (this.hasRewardUnavailableMarker({ message: val })) return ''
      const token = String(val).trim().toLowerCase()
      if (token === 'null' || token === 'undefined') return ''
      // If it's already a short time string like "15:00", return as-is
      if (typeof val === 'string' && /^\d{1,2}:\d{2}/.test(val)) return val
      // Try parsing as date
      const d = new Date(val)
      if (!isNaN(d.getTime())) {
        return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')
      }
      return String(val)
    },

    // Format roundKey like "202603191500" → "2026-03-19 15:00"
    formatRoundKey(key) {
      const str = String(key)
      if (!str || str.length < 12) return str
      const month = str.slice(4, 6)
      const day = str.slice(6, 8)
      const hour = str.slice(8, 10)
      const min = str.slice(10, 12)
      return month + '-' + day + ' ' + hour + ':' + min
    },

    // Calculate remaining seconds in active round from endTime
    calcRoundTimeLeft() {
      if (this.rpData && this.rpData.endTime) {
        const end = new Date(this.rpData.endTime).getTime()
        if (isNaN(end)) {
          this.roundTimeLeft = 0
          return
        }
        const now = Date.now()
        const diff = Math.floor((end - now) / 1000)
        this.roundTimeLeft = diff > 0 ? diff : 0
      } else {
        this.roundTimeLeft = 0
      }
    },

    startRoundTimer() {
      if (this.roundTimer) clearInterval(this.roundTimer)
      this.roundTimer = setInterval(() => {
        if (this.roundTimeLeft > 0) {
          this.roundTimeLeft--
        } else {
          clearInterval(this.roundTimer)
          this.roundTimer = null
          // Round ended, re-fetch
          this.initRedPacket()
        }
      }, 1000)
    },

    goBack() {
      this.$router.go(-1)
    },

    goDeposit() {
      this.$jumpTo('/rechargeCont')
    },
    async fetchHistory() {
      this.historyPage = 1
      this.historyFinished = false
      this.historyLoading = true
      try {
        const res = await RedPacketReceiveRecord({ pageIndex: this.historyPage, pageSize: this.historyPageSize })
        if (isOkResponse(res) && this.hasHistoryReadData(responseContent(res))) {
          this.historyList = this.mergeHistoryList([], this.extractHistoryList(responseContent(res)))
          if (this.historyList.length < this.historyPageSize) {
            this.historyFinished = true
          }
        } else if (this.isRewardNeedLogin(res)) {
          this.requireLogin()
        } else {
          this.historyFinished = true
          this.$toast({ message: this.failMessage(res), icon: 'cross' })
        }
      } catch (e) {
        this.historyFinished = true
        this.$toast({ message: this.failMessage(e, this.$lang.network_error || '活动请求失败，请稍后重试。'), icon: 'cross' })
      } finally {
        this.historyLoading = false
      }
    },
    async loadMoreHistory() {
      if (this.historyLoading || this.historyFinished) return
      this.historyLoading = true
      this.historyPage++
      try {
        const res = await RedPacketReceiveRecord({ pageIndex: this.historyPage, pageSize: this.historyPageSize })
        if (isOkResponse(res) && this.hasHistoryReadData(responseContent(res))) {
          const list = this.extractHistoryList(responseContent(res))
          this.historyList = this.mergeHistoryList(this.historyList, list)
          if (list.length < this.historyPageSize) {
            this.historyFinished = true
          }
        } else if (this.isRewardNeedLogin(res)) {
          this.requireLogin()
        } else {
          this.historyFinished = true
          this.$toast({ message: this.failMessage(res), icon: 'cross' })
        }
      } catch (e) {
        this.historyFinished = true
        this.$toast({ message: this.failMessage(e, this.$lang.network_error || '活动请求失败，请稍后重试。'), icon: 'cross' })
      } finally {
        this.historyLoading = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
@rp-bg: @vk-bg;        /* 页面底 */
@rp-card: @vk-surface; /* 卡片面 */
@rp-red: @brand;       /* 红包主色→火焰橙(品牌火焰红,招财喜庆且统一) */
@rp-gold: #ffb020;     /* 金币/金额→金 */
@rp-green: #15c26b;    /* 领取成功→success 绿(语义) */
@rp-border: rgba(255, 255, 255, 0.06);

.rp-page {
  min-height: 100vh;
  background: @rp-bg;
  padding-bottom: 80px;
}

// ====== HEADER ======
.rp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 50;
  background: fade(@rp-bg, 90%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid @rp-border;

  &__back {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 700;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    font-size: var(--vk-fs-xs);
    color: @rp-red;
    font-weight: 600;
  }
}

// ====== LOADING ======
.rp-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

// ====== STATE SCREENS ======
.rp-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px 32px;
  text-align: center;

  &__icon-wrap {
    position: relative;
    margin-bottom: 20px;
  }

  &__icon {
    width: 80px;
    height: 80px;
    object-fit: contain;
    margin-bottom: 16px;

    &--locked {
      opacity: 0.5;
      filter: grayscale(0.3);
      margin-bottom: 0;
    }

    &--pulse {
      animation: rp-pulse 2s ease-in-out infinite;
    }

    &--grey {
      filter: grayscale(1);
      opacity: 0.4;
    }
  }

  &__lock {
    position: absolute;
    bottom: 0;
    right: -4px;
    background: @rp-card;
    border-radius: 50%;
    padding: 4px;
  }

  &__title {
    font-size: var(--vk-fs-lg);
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &--green {
      color: @rp-green;
    }
  }

  &__desc {
    font-size: var(--vk-fs-sm);
    color: var(--vk-t2);
    line-height: 1.5;
    max-width: 280px;
    margin-bottom: 24px;
  }

  &__sub {
    font-size: var(--vk-fs-xs);
    color: var(--vk-t3);
    margin-top: 16px;
  }
}

// ====== BUTTON ======
.rp-btn {
  border: none;
  border-radius: 12px;
  font-weight: 800;
  font-size: var(--vk-fs-md);
  padding: 14px 48px;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;

  &--deposit {
    background: @rp-red;
    color: #ffffff;
    box-shadow: 0 4px 0 #b91c1c, 0 8px 24px fade(@rp-red, 30%);

    &:active {
      transform: translateY(4px);
      box-shadow: none;
    }
  }
}

// ====== COUNTDOWN ======
.rp-countdown {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;

  &__block {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: @rp-card;
    border: 1px solid fade(@rp-red, 30%);
    border-radius: 10px;
    padding: 10px 16px;
    min-width: 60px;
  }

  &__num {
    font-size: 28px;
    font-weight: 900;
    color: @rp-red;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 0 12px fade(@rp-red, 40%);
  }

  &__label {
    font-size: 9px;
    color: var(--vk-t3);
    font-weight: 700;
    text-transform: uppercase;
    margin-top: 2px;
  }

  &__sep {
    font-size: var(--vk-fs-2xl);
    font-weight: 900;
    color: @rp-red;
  }
}

// ====== GAME SCREEN ======
.rp-game {
  padding: 16px;

  &__title {
    text-align: center;
    font-size: var(--vk-fs-xl);
    font-weight: 900;
    color: @rp-gold;
    text-transform: uppercase;
    letter-spacing: 2px;
    animation: rp-pulse 1.5s ease-in-out infinite;
    margin-bottom: 12px;
  }

  &__stats {
    display: flex;
    justify-content: space-between;
    background: @rp-card;
    border-radius: 10px;
    padding: 12px 16px;
    border: 1px solid @rp-border;
    margin-bottom: 8px;
  }

  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    &-label {
      font-size: var(--vk-fs-2xs);
      color: var(--vk-t3);
      text-transform: uppercase;
      font-weight: 700;
      letter-spacing: 1px;
    }

    &-value {
      font-size: var(--vk-fs-md);
      font-weight: 800;
      color: #ffffff;

      &--gold {
        color: @rp-gold;
      }
    }
  }

  &__progress {
    margin-bottom: 16px;

    &-track {
      width: 100%;
      height: 6px;
      background: @rp-card;
      border-radius: 999px;
      overflow: hidden;
    }

    &-fill {
      height: 100%;
      background: linear-gradient(to right, @rp-red, @rp-gold);
      border-radius: 999px;
      transition: width 0.3s ease;
    }
  }
}

// ====== RAIN AREA ======
.rp-rain {
  position: relative;
  width: 100%;
  height: 50vh;
  overflow: hidden;
  background: radial-gradient(ellipse at 50% 0%, fade(@rp-red, 8%) 0%, transparent 70%);
  border-radius: 16px;
  border: 1px solid fade(@rp-red, 15%);

  &__packet {
    position: absolute;
    top: -60px;
    cursor: pointer;
    animation: rp-fall linear forwards;
    z-index: 2;

    &--popped {
      animation: rp-pop 0.3s ease-out forwards !important;
    }
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 4px 8px rgba(255, 90, 31, 0.3));
    pointer-events: none;
  }

  &__reward {
    position: absolute;
    z-index: 10;
    font-size: var(--vk-fs-lg);
    font-weight: 900;
    color: @rp-gold;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    animation: rp-reward-float 1.5s ease-out forwards;
    pointer-events: none;
    white-space: nowrap;
  }
}

// ====== REWARD CARD ======
.rp-reward-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: @rp-card;
  border: 1px solid fade(@rp-gold, 25%);
  border-radius: 16px;
  padding: 20px 40px;
  margin-top: 16px;

  &__label {
    font-size: var(--vk-fs-xs);
    color: var(--vk-t2);
    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: 700;
  }

  &__amount {
    font-size: 36px;
    font-weight: 900;
    color: @rp-gold;
    text-shadow: 0 0 16px fade(@rp-gold, 30%);
  }
}

// ====== NEXT ROUND ======
.rp-next-round {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 24px;

  &__label {
    font-size: var(--vk-fs-xs);
    color: var(--vk-t3);
    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: 700;
  }

  &__time {
    font-size: var(--vk-fs-xl);
    font-weight: 900;
    color: @rp-red;
    font-variant-numeric: tabular-nums;
  }
}

// ====== INFO CARD ======
.rp-info-card {
  margin: 16px;
  background: @rp-card;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid @rp-border;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;

    & + & {
      border-top: 1px solid @rp-border;
    }
  }

  &__label {
    font-size: var(--vk-fs-xs);
    color: var(--vk-t3);
    font-weight: 600;
  }

  &__value {
    font-size: var(--vk-fs-sm);
    color: #ffffff;
    font-weight: 700;

    &--red {
      color: @rp-red;
      font-variant-numeric: tabular-nums;
    }
  }
}

// ====== RULES ======
.rp-rules {
  margin: 16px;
  background: @rp-card;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid @rp-border;

  &__title {
    font-size: var(--vk-fs-sm);
    font-weight: 800;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 12px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: var(--vk-fs-xs);
    color: var(--vk-t2);
    line-height: 1.4;
  }

  &__num {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: fade(@rp-red, 15%);
    color: @rp-red;
    font-size: var(--vk-fs-2xs);
    font-weight: 800;
  }
}

// ====== HISTORY POPUP ======
.rp-history {
  padding: 20px 16px;

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 800;
    color: #ffffff;
    text-align: center;
    margin-bottom: 20px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 40px 0;

    p {
      font-size: var(--vk-fs-sm);
      color: var(--vk-t3);
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255, 255, 255, 0.03);
    padding: 12px;
    border-radius: 10px;
    border: 1px solid @rp-border;
  }

  &__left {
    flex-shrink: 0;
  }

  &__mid {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__round {
    font-size: var(--vk-fs-sm);
    font-weight: 600;
    color: #ffffff;
  }

  &__time {
    font-size: var(--vk-fs-xs);
    color: var(--vk-t3);
  }

  &__order {
    font-size: var(--vk-fs-2xs);
    color: #4b5563;
    word-break: break-all;
  }

  &__right {
    flex-shrink: 0;
    text-align: right;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__amount {
    font-size: var(--vk-fs-15);
    font-weight: 800;
    color: @rp-gold;
  }

  &__status {
    font-size: var(--vk-fs-2xs);
    color: var(--vk-t3);
  }

  &__more {
    text-align: center;
    padding: 14px;
    color: @rp-red;
    font-size: var(--vk-fs-sm);
    font-weight: 600;
    cursor: pointer;
  }
}

// ====== ANIMATIONS ======
@keyframes rp-fall {
  0% {
    transform: translateY(-60px) rotate(0deg);
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(55vh) rotate(15deg);
    opacity: 0;
  }
}

@keyframes rp-pop {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.8;
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
}

@keyframes rp-reward-float {
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateY(-80px) scale(1.3);
    opacity: 0;
  }
}

@keyframes rp-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}
</style>
